import { Component, computed, input, output } from "@angular/core";
import {
  HlmDropdownMenu,
  HlmDropdownMenuGroup,
  HlmDropdownMenuItem,
  HlmDropdownMenuTrigger,
} from "@o2-fe/ui";
import { StatusBadgeDirective } from "../../directives/status-badge.directive";
import { StatusLabelPipe } from "../../pipes/status-label.pipe";
import { HlmButtonImports } from "@o2-fe/ui";

/**
 * Status selector component with dropdown menu
 *
 * Features:
 * - Shows current status as a colored badge
 * - Opens menu with available status options on click (only if availableStatuses is not empty)
 * - Emits statusChange event when a new status is selected
 * - Smart cursor behavior: pointer when clickable, default when no transitions available
 * - Does NOT modify visual style when no statuses available (no disabled state)
 * - Supports two variants: "inline" (for tables) and "outlined" (for forms with border)
 *
 * Usage:
 * <app-status-selector
 *   [currentStatus]="user.status"
 *   [availableStatuses]="['active', 'inactive']"
 *   [variant]="'outlined'"
 *   (statusChange)="onStatusChange($event)"
 * />
 *
 * Then in parent component:
 * onStatusChange(newStatus: string): void {
 *   // Get the row item and make API call
 *   this.apiService.updateUser(item.id, { status: newStatus }).subscribe();
 * }
 *
 * Note: If availableStatuses is empty, the dropdown will not open and cursor will be default,
 * but the badge will remain fully visible (no disabled styling)
 */
@Component({
  selector: "app-status-selector",
  standalone: true,
  imports: [
    HlmDropdownMenu,
    HlmDropdownMenuGroup,
    HlmDropdownMenuItem,
    HlmDropdownMenuTrigger,
    StatusBadgeDirective,
    StatusLabelPipe,
    HlmButtonImports,
  ],
  template: `
    <button
      hlmBtn
      [variant]="variant() === 'outlined' ? 'outline' : 'ghost'"
      [hlmDropdownMenuTrigger]="isInteractive() ? menu : null"
      [disabled]="disabled()"
      (click)="$event.stopPropagation()"
      [class]="getTriggerClasses()"
    >
      <span statusBadge [status]="currentStatus()">
        @if (customLabels()) {
          {{ getStatusLabel(currentStatus()) }}
        } @else {
          {{ currentStatus() | statusLabel }}
        }
      </span>
    </button>

    <ng-template #menu>
      <div
        hlmDropdownMenu
        [sideOffset]="0"
        [class]="getMenuClasses()"
        class="mt-0.5"
      >
        <div hlmDropdownMenuGroup>
          @for (status of otherStatuses(); track status) {
            <button
              hlmDropdownMenuItem
              class="flex items-center gap-2"
              (click)="onStatusSelect(status)"
            >
              <span statusBadge [status]="status">
                @if (customLabels()) {
                  {{ getStatusLabel(status) }}
                } @else {
                  {{ status | statusLabel }}
                }
              </span>
            </button>
          }
        </div>
      </div>
    </ng-template>
  `,
  styles: [],
})
export class StatusSelectorComponent {
  // Inputs
  currentStatus = input.required<string>();
  availableStatuses = input.required<string[]>();
  disabled = input<boolean>(false);
  variant = input<"inline" | "outlined">("inline");
  // Optional custom labels map (status -> label)
  customLabels = input<Record<string, string>>();

  // Output - emits only the new status value
  statusChange = output<string>();

  // Computed - filter out current status from menu options
  protected readonly otherStatuses = computed(() =>
    this.availableStatuses().filter(
      (status) => status !== this.currentStatus(),
    ),
  );

  // Computed - check if there are any available status transitions
  protected readonly hasAvailableStatuses = computed(
    () => this.availableStatuses().length > 0,
  );

  // Computed - check if the selector is interactive (not disabled and has statuses)
  protected readonly isInteractive = computed(
    () => !this.disabled() && this.hasAvailableStatuses(),
  );

  protected getTriggerClasses(): string {
    const variant = this.variant();
    // For outlined variant, adapt to content without fixed width
    // For inline variant (tables), use w-full to fill the cell
    const widthClass = variant === "outlined" ? "" : "w-full";
    // For outlined variant, use transparent background like inputs
    const bgClass = variant === "outlined" ? "!bg-white" : "";
    const baseClasses = `${widthClass} ${bgClass} text-start flex justify-start px-3`;
    const cursorClass = this.isInteractive()
      ? "cursor-pointer"
      : "cursor-default";

    return `${baseClasses} ${cursorClass}`;
  }

  protected getMenuClasses(): string {
    const variant = this.variant();
    if (variant === "outlined") {
      // For outlined variant, adapt to content without fixed width
      return "rounded-md";
    }
    // Default inline variant (for tables)
    return "!min-w-32 !w-[calc(8rem+1px)] -ml-px rounded-none";
  }

  protected onStatusSelect(newStatus: string): void {
    this.statusChange.emit(newStatus);
  }

  protected getStatusLabel(status: string): string {
    const customLabels = this.customLabels();
    if (customLabels && customLabels[status]) {
      return customLabels[status];
    }
    return status; // Fallback to pipe behavior
  }
}
